import React, { useState, useEffect } from 'react';
import { makeStyles, withStyles } from 'utils/makeStylesAdapter';
import { Grid, Box, MenuItem, TextField, Button, Tooltip } from '@mui/material';
import { colors } from 'utils/colorsSteps';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { ModalSchedule } from '../../components';
import { generateUrl } from 'utils/googleCalendar/generateUrlIframe';
import { toast } from 'react-toastify';
import InputMask from 'react-input-mask';
import { addHoursAndMinutesInDate } from 'utils/date-time.utils';
import { KeyboardDatePicker } from 'components/DatePickers';
import { useCustomerConfig } from 'hooks/useCustomerConfig';
import TableHandleCertificates from '../TableHandleCertificates';
import { HandleIntimationMonitoring } from '../TableHandleCertificates';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

const CssTextField = withStyles(() => ({
  root: {
    backgroundColor: '#fff',
    borderRadius: 5,
    '& label.Mui-focused': {
      color: '#000',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: '#fff',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#000',
      },
      '&:hover fieldset': {
        borderColor: '#000',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#000',
      },
    },
  },
}))(TextField);

const useStyles = makeStyles(() => ({
  root: {
    height: '100%',
  },
  tabsRoot: {
    background: '#fff',
  },
  tabLabel: {
    color: '#222',
    fontWeight: 600,
    fontSize: 16,
    '&.Mui-selected': {
      color: '#222',
    },
  },
  btn: {
    color: '#fff',
    height: '100%',
    backgroundColor: colors.greenLight,
    '&:hover': {
      backgroundColor: colors.greenLight,
    },
  },
}));

const Form = props => {
  const { enableHearingForJuvenileOffenders } = useCustomerConfig();
  const {
    intimations,
    params,
    setParams,
    prosecutor,
    setOpenHearingModal,
    hasArchivingTask,
    inquiry,
    setWasCertificationFinalized,
    getIntimations,
    onRefresh,
  } = props;
  const intimationOptions = [
    hasArchivingTask
      ? {
          name: 'Arquivamento',
          value: 'ARCHIVE',
        }
      : {
          name: `Audiência${enableHearingForJuvenileOffenders ? '/Oitiva' : ''}`,
          value: 'HEARING',
        },
  ];

  const hearingTypes = [
    {
      name: 'Remota',
      value: false,
    },
    {
      name: 'Pessoalmente',
      value: true,
    },
  ];

  const classes = useStyles();

  const initialState = {
    typeIntimation: hasArchivingTask ? 'ARCHIVE' : 'HEARING',
    //intimationDeadline: '',
    hearingDate: '',
    hourHearing: '',
    isInPerson: false,
  };

  const [formState, setFormState] = useState({
    isValid: false,
    values: { ...initialState },
    touched: {},
    errors: {},
  });

  const [openSchedule, setOpenSchedule] = useState(false);
  const [url, setUrl] = useState('');
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (!params) {
      setFormState(formState => ({
        ...formState,
        values: { ...initialState },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleChange = event => {
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]:
          event.target.type === 'checkbox' ? event.target.checked : event.target.value,
      },
    }));
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    if (newValue === 1) {
      getIntimations();
    }
  };

  const handleSubmit = event => {
    event.preventDefault();

    const {
      typeIntimation,
      /*intimationDeadline,*/ hearingDate,
      hourHearing,
      isInPerson,
    } = formState.values;

    if (typeIntimation === '' || !typeIntimation) {
      toast.warn('Informe um tipo de intimação');
      return;
    }

    if (!hearingDate) {
      toast.warn('Informe a data da intimação');
      return;
    }

    if (!hourHearing || hourHearing.length !== 5) {
      toast.warn('Informe o horário da intimação 00:00');
      return;
    }

    let hora = hourHearing.substring(0, 2);
    let min = hourHearing.substring(3, 5);

    let manipulateHearingDate = addHoursAndMinutesInDate(hearingDate, hora, min);

    let params = {
      hearingDate: manipulateHearingDate,
      isInPerson: isInPerson,
    };

    setParams(params);
    setOpenHearingModal(true);
  };

  const handleOpenSchedule = () => {
    if (prosecutor) {
      setUrl(generateUrl(prosecutor.email));
      setOpenSchedule(true);
    } else {
      toast.warn('Não há promotor vinculado a este inquérito');
      return;
    }
  };

  return (
    <Box className={classes.root}>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={1}>
          <Grid size={{ xl: 12, lg: 12, md: 12, sm: 12, xs: 12 }}>
            <CssTextField
              fullWidth
              select
              size="small"
              variant="filled"
              label="Tipo da intimação"
              name="typeIntimation"
              onChange={handleChange}
              value={formState.values.typeIntimation || ''}
            >
              {intimationOptions.map(item => (
                <MenuItem key={item.name} value={item.value}>
                  {item.name}
                </MenuItem>
              ))}
            </CssTextField>
          </Grid>
          {formState.values.typeIntimation === 'ARCHIVE' ? (
            <>
              <Grid size={12}>
                <Tabs
                  value={tabIndex}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                  style={{ marginBottom: 16 }}
                  classes={{ root: classes.tabsRoot }}
                >
                  <Tab label="Certidões" />
                  <Tab label="Acompanhamento das intimações" />
                </Tabs>
                {tabIndex === 0 && (
                  <TableHandleCertificates
                    inquiry={inquiry}
                    setWasCertificationFinalized={setWasCertificationFinalized}
                    intimations={intimations}
                    getIntimations={getIntimations}
                    onRefresh={onRefresh}
                  />
                )}
                {tabIndex === 1 && (
                  <HandleIntimationMonitoring
                    inquiry={inquiry}
                    intimations={intimations}
                    getIntimations={getIntimations}
                  />
                )}
              </Grid>
            </>
          ) : (
            <>
              <Grid size={{ xl: 6, lg: 6, md: 6, sm: 6, xs: 12 }}>
                <KeyboardDatePicker
                  variant="inline"
                  inputVariant="filled"
                  name="hearingDate"
                  label={`Data da audiência${enableHearingForJuvenileOffenders ? '/oitiva' : ''}`}
                  value={formState.values.hearingDate || null}
                  minDate={new Date()}
                  size="small"
                  onChange={newDate =>
                    setFormState(formState => ({
                      ...formState,
                      values: {
                        ...formState.values,
                        hearingDate: newDate,
                      },
                    }))
                  }
                  component={CssTextField}
                />
              </Grid>
              <Grid size={{ xl: 6, lg: 6, md: 6, sm: 6, xs: 12 }}>
                <InputMask
                  onChange={handleChange}
                  value={formState.values.hourHearing || ''}
                  mask="99:99"
                  maskChar={null}
                >
                  {inputProps => (
                    <CssTextField
                      {...inputProps}
                      label="Hora"
                      name="hourHearing"
                      variant="filled"
                      fullWidth
                      size="small"
                    />
                  )}
                </InputMask>
              </Grid>
              <Grid size={{ xl: 6, lg: 6, md: 6, sm: 6, xs: 12 }}>
                <CssTextField
                  label={`Formato da audiência${
                    enableHearingForJuvenileOffenders ? '/oitiva' : ''
                  }`}
                  select
                  name="isInPerson"
                  variant="filled"
                  size="small"
                  value={formState.values.isInPerson}
                  onChange={handleChange}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                >
                  {hearingTypes.map(item => (
                    <MenuItem key={item.name} value={item.value}>
                      {item.name}
                    </MenuItem>
                  ))}
                </CssTextField>
              </Grid>
              <Grid size={{ xl: 3, lg: 3, md: 3, sm: 6, xs: 12 }}>
                <Button variant="contained" fullWidth className={classes.btn} type="submit">
                  Intimar
                </Button>
              </Grid>
              <Grid size={{ xl: 3, lg: 3, md: 3, sm: 6, xs: 12 }}>
                <Tooltip title="Consultar Agenda do promotor" placement="bottom">
                  <Button
                    variant="contained"
                    fullWidth
                    className={classes.btn}
                    startIcon={<DateRangeIcon style={{ color: '#fff' }} />}
                    onClick={handleOpenSchedule}
                  >
                    Agenda
                  </Button>
                </Tooltip>
              </Grid>
            </>
          )}
        </Grid>
      </form>
      <ModalSchedule
        open={openSchedule}
        onClose={() => setOpenSchedule(false)}
        url={url}
        formState={formState}
        setFormState={setFormState}
      />
    </Box>
  );
};

export default Form;
